import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import { Search } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Placeholder, SortableTableHeader } from 'components';
import SearchForm from 'features/feedback/SearchForm';
import {
	useAlerts,
	useFeedbackAttempts,
	useFeedbackRequests,
	useNavigation,
	useSelected,
	useSubmissions
} from 'hooks';
import FeedbackAttemptItem from './FeedbackAttemptItem';

const TABLE_HEADERS = [
	{
		value: 'image_url',
		label: 'Image',
		sortable: false,
		hidden: false
	},

	{
		value: 'title',
		label: 'Title',
		sortable: false,
		hidden: false
	},

	{
		value: 'user',
		label: 'User',
		sortable: false,
		hidden: false
	},

	{
		value: 'skill_categories',
		label: 'Skills',
		sortable: false,
		hidden: false
	},

	{
		value: 'created_at',
		label: 'Created On',
		sortable: true,
		hidden: false
	}
];

const FeedbackAttemptList = ({ title, status = null }) => {
	const navigate = useNavigate();

	const {
		findFeedbackRequests,
		feedbackRequests,
		isLoading,
		deleteFeedbackRequest,
		page,
		paginate,
		query,
		numPages,
		perPage,
		isEmpty,
		sortKey,
		sortDirection,
		handleSort
	} = useFeedbackRequests({ url: '' });
	const { deleteSubmission } = useSubmissions({
		url: '/api/v1/admin/submissions'
	});

	const handleClick = (feedbackAttempt) => {
		navigate(
			`/feedback_attempt/${feedbackAttempt.id}?type=${feedbackAttempt.type}&status=${status}`
		);
	};
	const handleEditClick = (feedbackAttempt) => {
		navigate(
			`/feedback_attempt/${feedbackAttempt.id}/edit?type=${feedbackAttempt.type}&status=${status}`
		);
	};

	const { selectedIds, handleSelect } = useSelected();

	const { showAlertSuccess } = useAlerts();

	const handleDeleteClick = async (feedbackAttempt) => {
		if (feedbackAttempt.type === 'TrainingPlan') {
			deleteFeedbackRequest(feedbackAttempt).finally(() => {
				findFeedbackRequests({
					filter: status
				});
			});
		} else {
			deleteSubmission(feedbackAttempt).finally(() => {
				findFeedbackRequests({
					filter: status
				});
			});
		}

		showAlertSuccess('Submission has been deleted');
	};

	const handleSearch = (params) => {
		const { keywords } = params;
		findFeedbackRequests({
			filter: status,
			keywords: keywords
		});
	};

	useEffect(() => {
		findFeedbackRequests({
			filter: status
		});
	}, []);

	return (
		<>
			<Paper>
				<SearchForm
					isLoading={isLoading}
					query={query}
					handleSearch={handleSearch}
				/>
				{!isEmpty ? (
					<TableContainer component={Paper} elevation={0}>
						<Table>
							<TableHead>
								<TableRow>
									{TABLE_HEADERS.filter((h) => !h.hidden).map((header, idx) => (
										<SortableTableHeader
											key={idx}
											handleSort={handleSort}
											value={`feedback_attempts.${header.value}`}
											sortable={header.sortable}
											sortKey={sortKey}
											sortDirection={sortDirection}
										>
											{header.label}
										</SortableTableHeader>
									))}
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{feedbackRequests.map((feedbackAttempt, idx) => (
									<FeedbackAttemptItem
										key={idx}
										feedbackAttempt={feedbackAttempt}
										isSelectable={false}
										selectedIds={selectedIds}
										handleSelect={handleSelect}
										handleClick={handleClick}
										handleDeleteClick={handleDeleteClick}
										handleEditClick={handleEditClick}
										status={status}
									/>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Placeholder
						icon={<Search />}
						title="No Submissions"
						subtitle="Please try another search"
					/>
				)}
			</Paper>
			{!isEmpty && (
				<Box my={1}>
					<Pagination
						onChange={(ev, page) => paginate(page)}
						color="primary"
						size="small"
						shape="rounded"
						page={page}
						count={numPages}
					/>
				</Box>
			)}
		</>
	);
};

export default FeedbackAttemptList;
